import { useEffect, useRef, useState } from 'react';

export interface Ponto {
  id: string;
  /** Idade em meses na avaliacao. */
  x: number;
  y: number;
  rotuloX: string;
  rotuloY: string;
}

/** Faixa do esperado para a idade (media ± 1 dp), atras da linha do animal. */
export interface PontoDeReferencia {
  x: number;
  min: number;
  media: number;
  max: number;
}

const ALTURA = 220;
const M = { topo: 16, dir: 16, base: 36, esq: 52 };

function useLargura<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [largura, setLargura] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setLargura(e?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, largura] as const;
}

/** Ate 4 marcas "redondas" cobrindo [min, max]. */
function marcas(min: number, max: number): number[] {
  const bruto = (max - min) / 3 || 1;
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const passo = [1, 2, 2.5, 5, 10].map((k) => k * potencia).find((p) => p >= bruto) ?? bruto;
  const r: number[] = [];
  for (let v = Math.floor(min / passo) * passo; v <= max + passo / 2; v += passo) r.push(Number(v.toFixed(6)));
  return r;
}

/**
 * Evolucao de uma medida ao longo da vida. Uma serie so: o titulo da secao diz
 * o que e, sem legenda. Linha e pontos na cor da marca (maximo contraste nos dois
 * temas); Ocean so no anel do ponto da ficha selecionada.
 */
export function GraficoEvolucao({
  pontos,
  selecionado,
  onSelecionar,
  titulo,
  referencia = [],
}: {
  pontos: Ponto[];
  selecionado: string | null;
  onSelecionar: (id: string) => void;
  titulo: string;
  referencia?: readonly PontoDeReferencia[];
}) {
  const [ref, largura] = useLargura<HTMLDivElement>();
  const [foco, setFoco] = useState<string | null>(null);

  const xs = [...pontos.map((p) => p.x), ...referencia.map((r) => r.x)];
  const ys = [...pontos.map((p) => p.y), ...referencia.flatMap((r) => [r.min, r.max])];
  const folgaY = (Math.max(...ys) - Math.min(...ys)) * 0.15 || Math.abs(ys[0] ?? 1) * 0.05 || 1;
  const ticksY = marcas(Math.min(...ys) - folgaY, Math.max(...ys) + folgaY);
  const [y0, y1] = [ticksY[0] ?? 0, ticksY.at(-1) ?? 1];
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];

  const w = Math.max(largura, 240);
  const sx = (x: number) => M.esq + (x1 === x0 ? (w - M.esq - M.dir) / 2 : ((x - x0) / (x1 - x0)) * (w - M.esq - M.dir));
  const sy = (y: number) => M.topo + (1 - (y - y0) / (y1 - y0 || 1)) * (ALTURA - M.topo - M.base);

  const ativo = pontos.find((p) => p.id === foco);
  const caminho = pontos.map((p, i) => `${i ? 'L' : 'M'}${sx(p.x)},${sy(p.y)}`).join(' ');
  const faixa = referencia.length
    ? `${referencia.map((r, i) => `${i ? 'L' : 'M'}${sx(r.x)},${sy(r.max)}`).join(' ')} ${[...referencia]
        .reverse()
        .map((r) => `L${sx(r.x)},${sy(r.min)}`)
        .join(' ')} Z`
    : '';
  const linhaMedia = referencia.map((r, i) => `${i ? 'L' : 'M'}${sx(r.x)},${sy(r.media)}`).join(' ');

  const maisProximo = (px: number) =>
    pontos.reduce((a, b) => (Math.abs(sx(b.x) - px) < Math.abs(sx(a.x) - px) ? b : a), pontos[0]!);

  return (
    <div ref={ref} className="relative">
      {largura > 0 && (
        <svg
          width={w}
          height={ALTURA}
          role="img"
          aria-label={`${titulo}: ${pontos.map((p) => `${p.rotuloY} aos ${p.rotuloX}`).join('; ')}${
            referencia.length ? '. Faixa sombreada: esperado para a idade, pela média dos animais medidos.' : ''
          }`}
          className="block touch-none select-none"
          onPointerMove={(e) => setFoco(maisProximo(e.nativeEvent.offsetX).id)}
          onPointerLeave={() => setFoco(null)}
          onClick={(e) => onSelecionar(maisProximo(e.nativeEvent.offsetX).id)}
        >
          {ticksY.map((t) => (
            <g key={t}>
              <line x1={M.esq} x2={w - M.dir} y1={sy(t)} y2={sy(t)} className="stroke-borda" strokeWidth={1} />
              <text x={M.esq - 8} y={sy(t)} dy="0.32em" textAnchor="end" className="numero fill-apagado text-xs">
                {t.toLocaleString('pt-BR')}
              </text>
            </g>
          ))}
          {pontos.map((p, i) => (
            <text
              key={p.id}
              x={sx(p.x)}
              y={ALTURA - 12}
              // Pontas ancoradas para dentro: o rotulo nao sai do grafico.
              textAnchor={pontos.length > 1 && i === 0 ? 'start' : pontos.length > 1 && i === pontos.length - 1 ? 'end' : 'middle'}
              className="numero fill-apagado text-xs"
            >
              {p.rotuloX}
            </text>
          ))}

          {faixa && (
            <>
              <path d={faixa} className="fill-destaque/12" stroke="none" />
              <path d={linhaMedia} fill="none" className="stroke-destaque" strokeWidth={1.5} strokeDasharray="5 4" />
            </>
          )}

          {ativo && (
            <line x1={sx(ativo.x)} x2={sx(ativo.x)} y1={M.topo} y2={ALTURA - M.base} className="stroke-apagado" strokeDasharray="3 3" />
          )}

          <path d={caminho} fill="none" className="stroke-marca" strokeWidth={2} strokeLinejoin="miter" />

          {pontos.map((p) => {
            const sel = p.id === selecionado;
            return (
              <g key={p.id}>
                {sel && <circle cx={sx(p.x)} cy={sy(p.y)} r={9} fill="none" className="stroke-destaque" strokeWidth={3} />}
                <circle cx={sx(p.x)} cy={sy(p.y)} r={5} className="fill-marca stroke-superficie" strokeWidth={2} />
              </g>
            );
          })}
        </svg>
      )}

      {ativo && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-(--radius-controle) border border-borda bg-superficie px-3 py-1.5 text-sm shadow-menu"
          // Preso dentro do grafico para nao vazar pelas bordas no celular.
          style={{ left: Math.min(Math.max(sx(ativo.x), 64), w - 64), top: sy(ativo.y) - 12 }}
        >
          <div className="numero font-semibold">{ativo.rotuloY}</div>
          <div className="text-xs text-apagado">aos {ativo.rotuloX}</div>
        </div>
      )}
    </div>
  );
}
